/**
 * Order total calculation — must stay in sync with backend src/utils/orderTotals.ts
 */

const UK_VAT_RATE = 0.2;

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface PriceableItem {
  price: number;
  quantity: number;
}

/**
 * Calculate goods net total (sum of all line totals before delivery/VAT)
 * Applies rounding to prevent floating-point display issues
 * Matches backend order calculation logic
 */
export function calculateGoodsNet(items: PriceableItem[]): number {
  if (!items || items.length === 0) {
    return 0;
  }
  
  const total = items.reduce((sum, item) => {
    const price = typeof item.price === 'number' && !isNaN(item.price) ? item.price : 0;
    const quantity = typeof item.quantity === 'number' && !isNaN(item.quantity) ? item.quantity : 0;
    return sum + price * quantity;
  }, 0);
  
  return roundMoney(total);
}

export interface LineItemTotals {
  itemGross: number;
  itemNet: number;
  itemTax: number;
  lineGross: number;
  lineNet: number;
  lineTax: number;
}

export function calculateLineTotals(
  quantity: number,
  unitPriceGross: number | null,
  unitPriceNet: number | null,
  taxRatePercent: number
): LineItemTotals {
  let itemPrice: number;
  let itemVat: number;
  let itemGross: number;

  if (unitPriceGross !== null && unitPriceGross !== undefined) {
    itemGross = unitPriceGross;
    itemPrice = itemGross / (1 + taxRatePercent);
    itemVat = itemGross - itemPrice;
  } else if (unitPriceNet !== null && unitPriceNet !== undefined) {
    itemPrice = unitPriceNet;
    itemVat = itemPrice * taxRatePercent;
    itemGross = itemPrice + itemVat;
  } else {
    throw new Error('Either itemGross or itemNet must be provided');
  }

  return {
    itemGross,
    itemNet: itemPrice,
    itemTax: itemVat,
    lineGross: itemGross * quantity,
    lineNet: itemPrice * quantity,
    lineTax: itemVat * quantity,
  };
}

export interface OrderLineTotalInput {
  quantity: number;
  itemNet: number;
  taxRatePercent: number;
}

export interface OrderTotalsResult {
  goodsNet: number;
  goodsVat: number;
  goodsGross: number;
  deliveryNet: number;
  deliveryVat: number;
  deliveryGross: number;
  epFlatFee: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

export function calculateOrderTotals(options: {
  lines: OrderLineTotalInput[];
  deliveryGross: number;
  isZeroVat: boolean;
  epFlatFee: number;
  isWorldpayTestOrder?: boolean;
}): OrderTotalsResult {
  const { lines, deliveryGross, isZeroVat, epFlatFee, isWorldpayTestOrder = false } = options;

  if (isWorldpayTestOrder) {
    const goodsNet = lines.reduce(
      (sum, line) => sum + line.itemNet * line.quantity,
      0
    );
    return {
      goodsNet,
      goodsVat: 0,
      goodsGross: goodsNet,
      deliveryNet: 0,
      deliveryVat: 0,
      deliveryGross: 0,
      epFlatFee: 0,
      totalNet: goodsNet,
      totalVat: 0,
      totalGross: goodsNet,
    };
  }

  const lineTotals = lines.map((line) =>
    calculateLineTotals(line.quantity, null, line.itemNet, line.taxRatePercent)
  );

  const goodsNet = lineTotals.reduce((sum, line) => sum + line.lineNet, 0);
  const goodsVat = lineTotals.reduce((sum, line) => sum + line.lineTax, 0);
  const goodsGross = lineTotals.reduce((sum, line) => sum + line.lineGross, 0);

  const deliveryNet = isZeroVat
    ? deliveryGross
    : roundMoney(deliveryGross / (1 + UK_VAT_RATE));
  const deliveryVat = isZeroVat ? 0 : roundMoney(deliveryGross - deliveryNet);

  const totalNet = goodsNet + deliveryNet + epFlatFee;
  const totalVat = goodsVat + deliveryVat;
  const totalGross = goodsGross + deliveryGross + epFlatFee;

  return {
    goodsNet,
    goodsVat,
    goodsGross,
    deliveryNet,
    deliveryVat,
    deliveryGross,
    epFlatFee,
    totalNet,
    totalVat,
    totalGross,
  };
}
